import { useEffect, useRef, useState } from 'react'
import TitleHeader from '../TitleHeader/TitleHeader'
import Constants from '../../../app/utils/constants'

const cloudDevopsItems = Constants.cloudDevopsItems
const topRowItems = cloudDevopsItems.slice(0, 3)
const bottomRowItems = cloudDevopsItems.slice(3, 6)

// Website URLs keyed by item.name
const cloudDevopsLinks: Record<string, string> = {
  AWS: 'https://aws.amazon.com',
  Azure: 'https://azure.microsoft.com',
  'Google Cloud': 'https://cloud.google.com',
  Docker: 'https://www.docker.com',
  Kubernetes: 'https://kubernetes.io',
  Terraform: 'https://www.terraform.io',
}

const directionByColIndex = ['left', 'center', 'right'] as const

const CloudDevopsService = () => {
  // ── Cursor glow (scroll-aware) ────────────────────────────────────────────
  const containerRef = useRef<HTMLElement>(null)
  const blobRef = useRef<HTMLDivElement>(null)
  const [isInside, setIsInside] = useState(false)

  useEffect(() => {
    let clientX = 0, clientY = 0, currentX = 0, currentY = 0, rafId = 0
    let inside = false

    const container = containerRef.current
    if (!container) return

    const getRelativePos = () => {
      const rect = container.getBoundingClientRect()
      return { x: clientX - rect.left, y: clientY - rect.top }
    }

    const setPos = (x: number, y: number) => {
      if (blobRef.current)
        blobRef.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`
    }

    const handleMouseMove = (e: MouseEvent) => {
      clientX = e.clientX
      clientY = e.clientY
    }

    const handleEnter = (e: MouseEvent) => {
      clientX = e.clientX
      clientY = e.clientY
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
      inside = true
      setIsInside(true)
    }

    const handleLeave = () => {
      inside = false
      setIsInside(false)
    }

    const handleScroll = () => {
      if (!inside) return
      const { x, y } = getRelativePos()
      currentX = x; currentY = y
      setPos(x, y)
    }

    const animate = () => {
      if (inside) {
        const { x, y } = getRelativePos()
        currentX += (x - currentX) * 0.1
        currentY += (y - currentY) * 0.1
        setPos(currentX, currentY)
      }
      rafId = requestAnimationFrame(animate)
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseenter', handleEnter)
    container.addEventListener('mouseleave', handleLeave)
    window.addEventListener('scroll', handleScroll, { passive: true })
    rafId = requestAnimationFrame(animate)

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseenter', handleEnter)
      container.removeEventListener('mouseleave', handleLeave)
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])

  // ── Per-row visibility (row 0 and row 1 observed independently) ───────────
  const row0Ref = useRef<HTMLDivElement>(null)
  const row1Ref = useRef<HTMLDivElement>(null)
  const [row0Visible, setRow0Visible] = useState(false)
  const [row1Visible, setRow1Visible] = useState(false)

  useEffect(() => {
    const makeObserver = (el: HTMLDivElement | null, setter: (v: boolean) => void) => {
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => setter(entry.isIntersecting),
        { threshold: 0.2 },
      )
      obs.observe(el)
      return obs
    }
    const o0 = makeObserver(row0Ref.current, setRow0Visible)
    const o1 = makeObserver(row1Ref.current, setRow1Visible)
    return () => { o0?.disconnect(); o1?.disconnect() }
  }, [])

  const rowVisible = [row0Visible, row1Visible]

  // ── Divider lines visibility per row ─────────────────────────────────────
  const line0Ref = useRef<HTMLDivElement>(null)
  const line1Ref = useRef<HTMLDivElement>(null)
  const [line0Visible, setLine0Visible] = useState(false)
  const [line1Visible, setLine1Visible] = useState(false)

  useEffect(() => {
    const makeObserver = (el: HTMLDivElement | null, setter: (v: boolean) => void) => {
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => setter(entry.isIntersecting),
        { threshold: 0.15 },
      )
      obs.observe(el)
      return obs
    }
    const o0 = makeObserver(line0Ref.current, setLine0Visible)
    const o1 = makeObserver(line1Ref.current, setLine1Visible)
    return () => { o0?.disconnect(); o1?.disconnect() }
  }, [])


  const rows = [topRowItems, bottomRowItems]
  const lineVisible = [line0Visible, line1Visible]
  const rowSentinelRefs = [row0Ref, row1Ref]
  const lineSentinelRefs = [line0Ref, line1Ref]

  return (
    <section
      ref={containerRef}
      className="relative w-full overflow-hidden py-12 px-[8%] md:py-16"
    >
      {/* Cursor glow blob */}
      <div
        ref={blobRef}
        className={`pointer-events-none absolute w-[650px] h-[650px] rounded-full
          bg-[radial-gradient(circle_at_center,#9e5ddfba,transparent_70%)]
          blur-3xl z-0 transition-opacity duration-300 ease-out
          ${isInside ? 'opacity-50' : 'opacity-0'}`}
      />

      <style>{`
        /* Icon float */
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-7px); }
        }
        .devops-icon-float { animation: floatIcon 3s ease-in infinite; }

        /* Slide from left */
        .devops-slide-left {
          transform: translateX(-170px);
          opacity: 0;
          transition: transform 0.75s ease-in, opacity 0.6s ease-in;
        }
        /* Slide from right */
        .devops-slide-right {
          transform: translateX(170px);
          opacity: 0;
          transition: transform 0.75s ease-in, opacity 0.6s ease-in;
        }
        /* Scale from small (center item) */
        .devops-scale-hidden {
          transform: scale(0.1);
          opacity: 0;
          transition: transform 0.75s ease-in, opacity 0.6s ease-in;
        }
        /* In view — all directions */
        .devops-in {
          transform: translateX(0) scale(1);
          opacity: 1;
          transition: transform 0.75s ease-in, opacity 0.6s ease-in;
        }

        /* Divider line: scale from 0 → 1 */
        .devops-line-hidden {
          transform: scaleY(0);
          opacity: 0;
          transform-origin: center;
          transition: transform 0.7s ease-in, opacity 0.5s ease-in;
        }
        .devops-line-show {
          transform: scaleY(1);
          opacity: 1;
          transform-origin: center;
          transition: transform 0.7s ease-in, opacity 0.5s ease-in;
        }
      `}</style>

      {/* Header */}
      <div className="relative z-10 mb-8 flex justify-center md:mb-10">
        <TitleHeader
          badgeText="Software we are using"
          titleLines={['Our Cloud & DevOps Services']}
          wrapperClassName="flex flex-col items-center text-center"
          titleClassName="font-rajdhani text-3xl font-semibold md:text-4xl lg:text-4xl xl:text-4xl 2xl:text-6xl"
          badgeClassName="px-8 py-2 text-sm font-rajdhani font-semibold uppercase md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
        />
      </div>

      {/* Rows */}
      <div className="relative z-10 flex w-full flex-col gap-8 sm:gap-10 md:gap-12">
        {rows.map((row, rowIndex) => {
          const isVisible = rowVisible[rowIndex]
          const isLineVisible = lineVisible[rowIndex]

          return (
            <div
              key={rowIndex}
              // Sentinel for this row's items
              ref={rowSentinelRefs[rowIndex]}
              className="relative flex w-full items-stretch justify-between"
            >
              {/* Invisible sentinel for the divider lines of this row */}
              <div
                ref={lineSentinelRefs[rowIndex]}
                aria-hidden
                className="pointer-events-none absolute inset-0"
              />

              {row.map((item, colIndex) => {
                const dir = directionByColIndex[colIndex] ?? 'center'

                // Choose hidden class based on direction
                let hiddenClass = 'devops-scale-hidden'
                if (dir === 'left') hiddenClass = 'devops-slide-left'
                if (dir === 'right') hiddenClass = 'devops-slide-right'

                return (
                  <div key={item.name} className="flex min-h-0 min-w-0 flex-1 items-stretch">

                    {/* Item */}
                    <div
                      className={`flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center px-2
                        ${isVisible ? 'devops-in' : hiddenClass}`}
                      style={{
                        transitionDelay: isVisible
                          ? `${colIndex === 1 ? 0 : colIndex * 0.1}s`
                          : '0s',
                      }}
                    >
                      <a
                        href={cloudDevopsLinks[item.name] ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex flex-col items-center cursor-pointer"
                      >
                        <div className="rounded-full p-[1.25px] bg-[linear-gradient(138.94deg,#745EF0_0%,rgba(116,94,240,0.1)_50%,#745EF0_100%)]
                          transition-transform duration-300 group-hover:scale-110">
                          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0E0924]
                            sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-20 lg:w-20 xl:h-25 xl:w-25 2xl:h-32 2xl:w-32">
                            <img
                              src={item.icon}
                              alt={item.name}
                              className="devops-icon-float h-8 w-8 object-contain sm:h-10 sm:w-10 md:h-12 md:w-12 lg:h-10 lg:w-10 xl:h-12 xl:w-12 2xl:h-15 2xl:w-15"
                            />
                          </div>
                        </div>

                        <p className="mt-3 text-center font-gilroy-bold text-sm text-white
                          sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl
                          transition-colors duration-300 group-hover:text-purple-300
                          underline-offset-4 group-hover:underline">
                          {item.name}
                        </p>
                      </a>
                    </div>

                    {/* Divider line — scale small → full when row enters view */}
                    {colIndex < row.length - 1 && (
                      <div
                        aria-hidden
                        className={`pointer-events-none w-[2px] shrink-0 self-stretch
                          bg-[linear-gradient(180deg,transparent_0%,#EA892A_18%,#F0578B_82%,transparent_100%)]
                          ${isLineVisible ? 'devops-line-show' : 'devops-line-hidden'}`}
                        style={{
                          transitionDelay: isLineVisible ? `${colIndex * 0.15}s` : '0s',
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default CloudDevopsService